// The Excel report's legend (the customer's point 16): the "Colors" sheet, built as in the design
// (Designforslag_Week43) - a title, then sections under a dark bar, each line a sample cell that looks exactly as on
// the sheets and what it means beside it: the column categories, the task status, rows and lines, the tabs, the
// dates, and notes on this file (GPS fields the other way round, duplicates, tasks without a column, how dates show).
import { formatDisplayDate } from '../dateFormat';
import { STATUS } from './cellStatus';
import { frame, solid, styleCell } from './cellStyle';
import {
  CATEGORY_BORDER, CO_BORDER, GROUP_FILLS, GROUP_LABELS, HEADER_FONT, LAYOUT_TAB_COLOUR, NEXT_TASK_BORDER,
  PLANNER_HEADER_FONT, ROW_SHADES, TAB_COLOURS, WARNING_BORDER,
} from './colours';

export const COLORS_SHEET_NAME = 'Colors';
export const COLORS_TITLE = 'How to read this file';
export const TRIALS_IN_FILE = 'Trials in this file';

const BAR = 'FF404040';
const SAMPLE_BORDER = { style: 'thin', color: { argb: 'FFBFBFBF' } };
const TEXT_BORDER = { style: 'thin', color: { argb: 'FFD9D9D9' } };
const WHITE_BOLD = { bold: true, color: { argb: HEADER_FONT } };

// The categories in the design's order, each with a sample of its headers
const CATEGORIES = [
  ['identity', 'CO · Trial'], ['people', 'SD · PI · FT'], ['planner', 'prod · papir · person'], ['info', 'Crop · Host · …'],
  ['setup', 'Stacked out'], ['applications', 'A – L'], ['assessments', 'V00 – V14'], ['finish', 'Harvest · Pegs up'],
];

// The task status: [status, sample value, what it means]
const STATUSES = [
  [STATUS.done, new Date(Date.UTC(2026, 4, 14)), 'Done – done, a real date'],
  [STATUS.planned, 'P week 46 2026', 'Planned – Agrolab\'s planned date (P …)'],
  [STATUS.planned, 'P week 46 2026\nTI 01-11-2026', 'Planned and Time interval – both in the cell, P on top, TI below; the colour as for P'],
  [STATUS.interval, 'TI 14 DAA', 'Only a Time interval – the customer\'s agreed time (TI …)'],
  [STATUS.overdue, 'P 10-10-2026', 'Overdue – the date has passed, not done'],
  [STATUS.missing, '?', 'Missing info – ?, Maybe, TBD or empty (an empty EarthSample too)'],
  [STATUS.cancelled, 'Cancelled', 'Cancelled / Not needed – no longer to do (Cancelled struck through)'],
];

// The tabs: [sample, its sheets' tab colour, what they hold]
const ALL_DATA_TAB = ['All data', TAB_COLOURS['All data'], 'All trials, all columns'];
// The last note on this file (the customer's wording, 30-10-2026)
export const DATES_NOTE = 'A done task is shown as a real date. Planned dates start with P, time intervals start with TI.';
export const LAYOUT_TAB_TEXT = 'The chosen layout\'s columns, as the report on screen (one row per task when it has task fields)';
const WORK_SHEET_TABS = [
  ['Set-Up · Borderspray · Soil_Sample', TAB_COLOURS['Set-Up'], 'Set-up work'],
  ['Application', TAB_COLOURS.Application, 'Applications to do'],
  ['Assessment', TAB_COLOURS.Assessment, 'Assessments to do'],
  ['Pegup · Harvest', TAB_COLOURS.Pegup, 'Finishing the trial'],
];

/**
 * Adds the Colors sheet: the legend, and notes from the report's summary ({ trials, gpsSwapped, duplicates, notShown,
 * exportDate }). Without the work sheets (`workSheets` false) it leaves out what only they have: the planner's
 * columns, the next task's frame and their tabs. `layoutSheetName` is the layout sheet's tab, when there is one.
 */
export function addColorsSheet(workbook, summary, { workSheets = true, layoutSheetName = null } = {}) {
  const sheet = workbook.addWorksheet(COLORS_SHEET_NAME);
  sheet.properties.tabColor = { argb: TAB_COLOURS[COLORS_SHEET_NAME] };
  sheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 4, showGridLines: false }];
  sheet.pageSetup = { ...sheet.pageSetup, orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 0 };
  [3, 30, 70].forEach((width, i) => { sheet.getColumn(i + 1).width = width; });

  // The title and the subtitle over B:C
  const title = sheet.getCell('B2');
  title.value = COLORS_TITLE;
  title.font = { bold: true, size: 16, color: { argb: 'FF1F1F1F' } };
  sheet.mergeCells('B2:C2');
  const subtitle = sheet.getCell('B3');
  subtitle.value = `Agromate – ${workSheets ? 'work lists' : 'the trials'} in the Week 43 format. The colours are the same on every sheet. Exported ${formatDisplayDate(summary.exportDate)}.`;
  subtitle.font = { italic: true, size: 10, color: { argb: 'FF595959' } };
  sheet.mergeCells('B3:C3');
  // How many trials the file holds - sheet 1 has one row for each
  const count = sheet.getCell('B4');
  count.value = `${TRIALS_IN_FILE}: ${summary.trials}`;
  count.font = { bold: true, size: 11 };
  sheet.mergeCells('B4:C4');

  let r = 6;
  let number = 0;
  // A section's dark bar
  const section = (text) => {
    if (number > 0) r += 1;
    number += 1;
    const row = sheet.getRow(r);
    row.height = 22;
    for (const c of [2, 3]) {
      const cell = row.getCell(c);
      cell.fill = solid(BAR);
      cell.border = frame({ style: 'thin', color: { argb: BAR } });
    }
    const bar = row.getCell(2);
    bar.value = `${number}  ${text}`;
    bar.font = { ...WHITE_BOLD, size: 11 };
    bar.alignment = { vertical: 'middle', indent: 1 };
    r += 1;
  };
  // A line: the sample in B (set up by `sample`, over a centred cell with a light frame), what it means in C
  const line = (text, sample) => {
    const row = sheet.getRow(r);
    const b = row.getCell(2);
    b.alignment = { horizontal: 'center', vertical: 'middle' };
    b.border = frame(SAMPLE_BORDER);
    sample(b);
    // Two lines: a long explanation, or a sample on two lines (P and TI)
    row.height = text.length > 80 || String(b.value ?? '').includes('\n') ? 32 : 20;
    const c = row.getCell(3);
    c.value = text;
    c.font = { size: 10 };
    c.alignment = { vertical: 'middle', wrapText: true, indent: 1 };
    c.border = { right: TEXT_BORDER, top: TEXT_BORDER, bottom: TEXT_BORDER };
    r += 1;
  };

  section('Column categories (header colour)');
  for (const [group, sample] of CATEGORIES.filter(([group]) => workSheets || group !== 'planner')) {
    line(GROUP_LABELS[group], (cell) => {
      cell.value = sample;
      cell.fill = solid(GROUP_FILLS[group]);
      cell.font = { bold: true, color: { argb: group === 'planner' ? PLANNER_HEADER_FONT : HEADER_FONT } };
    });
  }

  section('Task cell status');
  for (const [status, sample, text] of STATUSES) {
    line(text, (cell) => {
      cell.value = sample;
      if (sample instanceof Date) cell.numFmt = 'dd-mm-yyyy';
      // As a task cell: P and TI on two lines
      if (String(sample).includes('\n')) cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      styleCell(cell, { status });
    });
  }
  if (workSheets) {
    line('Black frame and bold text: the sheet\'s next task – the first open task after the last done one (work sheets only)', (cell) => {
      cell.value = 'V03';
      cell.fill = solid('FFFFFFFF');
      cell.font = { bold: true };
      cell.border = frame(NEXT_TASK_BORDER);
    });
  }
  line('Red frame: needs attention – the trial has an address or soil sample but is not stacked out '
    + '(only when it has a Stacked out task), or has an address but no soil sample', (cell) => {
    cell.value = '?';
    styleCell(cell, { status: STATUS.missing });
    cell.border = frame(WARNING_BORDER);
  });

  section('Rows and lines');
  line('The rows switch between white and light grey for each new CO, so a customer\'s trials stand together', (cell) => {
    cell.fill = solid(ROW_SHADES[1]);
  });
  line('Thick horizontal line: a new CO starts here', (cell) => {
    cell.fill = solid(ROW_SHADES[0]);
    cell.border = { ...frame(SAMPLE_BORDER), top: CO_BORDER };
  });
  line('Thick vertical line: a new category of columns starts here', (cell) => {
    cell.fill = solid(ROW_SHADES[0]);
    cell.border = { ...frame(SAMPLE_BORDER), left: CATEGORY_BORDER };
  });

  section('Tabs');
  const layoutTab = layoutSheetName ? [[layoutSheetName, LAYOUT_TAB_COLOUR, LAYOUT_TAB_TEXT]] : [];
  for (const [sample, colour, text] of [ALL_DATA_TAB, ...layoutTab, ...(workSheets ? WORK_SHEET_TABS : [])]) {
    line(text, (cell) => {
      cell.value = sample;
      cell.fill = solid(colour);
      cell.font = { ...WHITE_BOLD, size: 9 };
    });
  }

  section('Dates');
  const dateLine = (sample, text) => line(text, (cell) => {
    cell.value = sample;
    cell.fill = solid('FFFFFFFF');
    cell.font = { bold: true };
  });
  dateLine('Date', 'The task is done (a real Excel date, DD-MM-YYYY)');
  dateLine('P …', 'Agrolab\'s planned date (Planned)');
  dateLine('TI …', 'The customer\'s agreed time (Time interval)');
  dateLine('Overdue', `The end of P / TI is before the export day (${formatDisplayDate(summary.exportDate)}): primo = 10th, medio = 20th, ultimo = last day, week = Sunday`);
  dateLine('…', 'A season or a month ends on its last day; anything else (7 DAA, BBCH 32, after harvest) is never overdue. P counts whenever there is one');

  section('Notes on this file');
  const note = (count, text) => line(text, (cell) => { cell.value = count; });
  note(summary.gpsSwapped, 'Trials whose two GPS fields are stored the other way round – each value is shown as stored, in its own column');
  note(summary.duplicates, 'Tasks with the same number as another task on the same trial – the file shows the done one, else the newest');
  note(summary.notShown, 'Visits and applications without a column (V15 and up, applications M–Z or numbered) – not in this file');
  line(DATES_NOTE, (cell) => {
    cell.value = 'Date · P · TI';
  });
  return sheet;
}
